import React, { useContext, useState } from "react";
import defaultAvatar from "../../assets/images/DefaultAvatar.webp";
import { useHistory } from "react-router-dom";
import "./style.scss";
import pushHistory from "../../functions/pushHistory";
import { GlobalContext } from "../../App";
import { getUser } from "../../api";

interface UsernameAvatarDisplayProps {
	user: any;
	newTab?: boolean;
	useAnchor?: boolean;
	style?: React.CSSProperties;
	authorClick?: boolean;
	suppressHover?: boolean;
}

/**
 * Component for displaying a user's avatar and username.
 * Includes interactive features such as displaying a hover card with user details and navigating to the user's profile page.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.user - User data object.
 * @param {boolean} [props.newTab=false] - Open the user's profile in a new tab if true.
 * @param {boolean} [props.useAnchor=true] - Use an anchor tag for the display if true.
 * @param {React.CSSProperties} [props.style={}] - Custom styles for the component.
 * @param {boolean} [props.authorClick=true] - Enable navigation to the user's profile on click.
 * @param {boolean} [props.suppressHover=false] - Suppress hover card display if true.
 *
 * @returns {JSX.Element} A JSX element displaying the user's avatar and username with interactive features.
 */

const UsernameAvatarDisplay = ({
	user,
	newTab = false,
	useAnchor = true,
	style = {},
	authorClick = true,
	suppressHover = false,
}: UsernameAvatarDisplayProps): JSX.Element => {
	const { hoverCardProps } = useContext(GlobalContext);
	const { setCardPosition, showCard, setShowCard, setHoverCardUser } =
		hoverCardProps;

	const [active, setActive] = useState(false);
	const [userCardInfo, setUserCardInfo] = useState(user);
	const [hasInfo, setHasInfo] = useState(user?.joinDate !== undefined);

	const history = useHistory();
	const tagName = useAnchor ? "a" : "span";

	// clicking the username
	const handleAuthorClick = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!authorClick) return;
		// check if the user is on a desktop device
		if (
			(window.innerWidth > 750 && window.innerHeight > 550) ||
			(showCard === userCardInfo?._id && active === true)
		) {
			if (newTab)
				return window.open(`/users/${userCardInfo?._id}`, "_blank");
			return pushHistory(
				history,
				`/users/${userCardInfo?._id}`,
				e.ctrlKey,
			);
		}

		if (setCardPosition === null || setShowCard === null) return;

		// function to trigger when on 
		if (!hasInfo) fetchUserData();
		setCardPosition({ x: e.clientX, y: e.clientY });
		setActive(true);
		return setShowCard(userCardInfo?._id);
	};

	const fetchUserData = () => {
		setHasInfo(true);
		getUser(userCardInfo?._id, false, { hovercard: 1 }).then((res) => {
			const user = res?.data?.user;
			if (!user) return;
			setUserCardInfo(user);
			setHoverCardUser(user);
		});
	};

	// hovering the username
	const handleHover = (e, show) => {
		e.preventDefault();
		e.stopPropagation();
		if (suppressHover) return;

		// set the user of the hover card
		if (setHoverCardUser === null) return;
		setHoverCardUser(userCardInfo);

		// don't fire if on mobile
		if (window.innerWidth <= 550 || window.innerHeight <= 550) return;

		if (setCardPosition === null || setShowCard === null) return;

		// show card
		if (show === true) {
			if (!hasInfo) fetchUserData();
			setCardPosition({ x: e.clientX, y: e.clientY });
			return setShowCard(userCardInfo?._id);
		}

		// hide card
		if (show === false) {
			return setShowCard(null);
		}
	};


	return React.createElement(tagName, {
		...(useAnchor && userCardInfo?._id && { href: `/users/${userCardInfo?._id}` }),
		onMouseEnter: (e) => handleHover(e, true),
		onMouseLeave: (e) => handleHover(e, false),
		onClick: (e) => handleAuthorClick(e),
		className: "username-avatar-display",
		style: {
			boxShadow: userCardInfo?.icon_glow
				? `0 0 3px 1px ${userCardInfo?.icon_glow}`
				: "none",
			borderColor: userCardInfo?.icon_glow,
			...style,
		},
		children: (
			<>
				<div
					className="icon-avatar"
					style={{
						borderColor: userCardInfo?.icon_outline,
						boxShadow: "unset",
					}}>
					<img
						alt="Author Avatar"
						className="icon-avatar__image"
						onError={(e) => {
							// @ts-ignore
							e.target.src = defaultAvatar;
						}}
						src={
							userCardInfo?.avatar
								? userCardInfo?.avatar
								: defaultAvatar
						}
					/>
				</div>
				<div>{userCardInfo?.username || <i className="gray">Unknown</i>}</div>
			</>
		),
	});
};

export default React.memo(UsernameAvatarDisplay);
